import React, { useState, useEffect } from 'react';
import {Smartphone, ExternalLink, Check, Share} from 'lucide-react';import {Button} from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
function isIOSSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  return isIOS && !isStandalone;
}

export default function IOSAppBanner({ title, subtitle, onContinue }) {
  const {
    t
  } = useTranslation();

  const [visible, setVisible] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    setVisible(isIOSSafari());
  }, []);

  if (!visible) return null;

  const handleOpenApp = () => {
    // Store the current share URL as a cookie so the PWA can pick it up on next launch
    const url = window.location.pathname;
    document.cookie = `eili_pending_share=${encodeURIComponent(url)};path=/;max-age=300;SameSite=Lax`;
    setOpened(true);
  };

  const handleContinue = () => {
    setVisible(false);
    if (onContinue) onContinue();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      data-testid="ios-app-banner"
    >
      <div
        className="w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        style={{ background: '#1e293b', border: '1px solid #334155' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3">
          <img src="/logo.png" alt={t('generated.iosappbanner_eili_e918')} className="h-12 w-12 rounded-xl" />
          <div>
            <h3 className="text-base font-semibold text-slate-100">{t('generated.iosappbanner_eili_e918')}</h3>
            <p className="text-xs text-slate-400">{t('generated.iosappbanner_biblical_research_companion_768f')}</p>
          </div>
        </div>

        {/* Content preview */}
        <div className="px-5 pb-4">
          <p className="text-sm font-medium text-slate-200 line-clamp-2">
            {title || 'Shared from Eili'}
          </p>
          {subtitle && (
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{subtitle}</p>
          )}
        </div>

        <div className="mx-5 h-px bg-slate-700/60" />

        {/* Actions */}
        <div className="px-5 py-4 space-y-2.5">
          {!opened ? (
            <Button
              onClick={handleOpenApp}
              className="w-full rounded-xl py-5 font-medium gap-2.5 text-sm"
              style={{ background: '#6366f1', color: '#fff' }}
              data-testid="ios-open-in-app-btn"
            >
              <Smartphone className="h-4 w-4" />
              {t('generated.iosappbanner_open_app_96aa')}
            </Button>
          ) : (
            <div className="rounded-xl py-4 px-4 text-center" style={{ background: '#0f172a', border: '1px solid #334155' }}>
              <Check className="h-5 w-5 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm font-medium text-slate-200">{t('generated.iosappbanner_now_open_eili_f5f7')}</p>
              <p className="text-[11px] text-slate-400 mt-1">{t('generated.iosappbanner_the_shared_content_2e08')}</p>
            </div>
          )}

          <Button
            onClick={handleContinue}
            variant="outline"
            className="w-full rounded-xl py-5 font-medium gap-2.5 text-sm"
            style={{ borderColor: '#334155', color: '#cbd5e1' }}
            data-testid="ios-continue-browser-btn"
          >
            <ExternalLink className="h-4 w-4" />
            {t('generated.iosappbanner_continue_in_browser_82ce')}
          </Button>
        </div>

        {/* Install hint */}
        <div className="px-5 pb-4">
          <div className="rounded-lg px-3 py-2.5 flex items-start gap-2.5" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
            <Share className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              {t('generated.iosappbanner_don_t_have_8f7a')} <span className="text-slate-400">{t('generated.iosappbanner_share_3732')}</span> {t('generated.iosappbanner_in_safari_then_fad9')} <span className="text-slate-400">{t('generated.iosappbanner_add_to_home_0a05')}</span> {t('generated.iosappbanner_to_install_eili_ef5a')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
